import React, { useEffect, useState, useRef } from "react";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import SweetAlert2 from "react-sweetalert2";
import { useSelector, useDispatch } from "react-redux";
import {
  setSelectedServiceOption,
  setOrderProcessingDate,
  setSelectedAddress,
} from "../../redux/publicReducer/actions";

// Import Mapbox CSS
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

const storeName = process.env.REACT_APP_RESTAURANT_NAME;
const storeAddress = process.env.REACT_APP_RESTAURANT_ADDRESS;

// Set your Mapbox access token here
// Get a free token from https://www.mapbox.com/
mapboxgl.accessToken =
  "pk.eyJ1Ijoicmd1dGFuYSIsImEiOiJjbWRlaHBscHkwMmMyMmtxeHVlNDQ3dGhmIn0.bfLXaXZgcJjcW52_z2nU_A";

export const DeliveryMap = (props) => {
  const [delAddress, setDelAddress] = useState("");
  const [map, setMap] = useState(null);
  const [geocoder, setGeocoder] = useState(null);

  const [chargePerMile, setChargePerMile] = useState([
    { miles: 0, charge: 4.0 },
    { miles: 1, charge: 4.0 },
    { miles: 2, charge: 4.0 },
    { miles: 3, charge: 4.0 },
    { miles: 4, charge: 4.0 },
    { miles: 5, charge: 4.0 },
    { miles: 6, charge: 4.0 },
    { miles: 7, charge: 4.0 },
    { miles: 8, charge: 4.0 },
    { miles: 9, charge: 4.0 },
    { miles: 10, charge: 4.0 },
    { miles: 11, charge: 4.0 },
    { miles: 12, charge: 4.0 },
    { miles: 13, charge: 4.0 },
    { miles: 14, charge: 4.0 },
    { miles: 15, charge: 4.0 },
    { miles: 16, charge: 4.0 },
    { miles: 17, charge: 4.0 },
    { miles: 18, charge: 4.0 },
    { miles: 19, charge: 4.0 },
    { miles: 20, charge: 4.0 },
    { miles: 21, charge: 4.0 },
    { miles: 22, charge: 4.0 },
    { miles: 23, charge: 4.0 },
    { miles: 24, charge: 4.0 },
    { miles: 25, charge: 4.0 },
  ]);

  const [swalProps, setSwalProps] = useState({});
  const dispatch = useDispatch();
  const { orderProcessingDate, orderProcessingType, selectedAddress } =
    useSelector((state) => state.public);

  const mapContainer = useRef();
  const mapRef = useRef();

  // Restaurant center coordinates
  const center = [-87.92341897797219, 42.386310397085964]; // lng, lat format for Mapbox
  const radiusInMiles = 25;

  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize map
    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: center,
      zoom: 11,
    });

    mapRef.current = mapInstance;

    mapInstance.on("load", () => {
      setMap(mapInstance);

      // Add geofence circle
      addGeofenceCircle(mapInstance);

      // Initialize geocoder
      const geocoderInstance = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        placeholder: "Type an address",
        proximity: {
          longitude: center[0],
          latitude: center[1],
        },
      });

      // Add geocoder to map
      mapInstance.addControl(geocoderInstance, "top-left");
      setGeocoder(geocoderInstance);

      // Handle address selection
      geocoderInstance.on("result", (e) => {
        handleAddressSelection(e.result, mapInstance);
      });

      // Handle map clicks
      mapInstance.on("click", (e) => {
        const clickedPoint = [e.lngLat.lng, e.lngLat.lat];
        const distance = calculateDistance(center, clickedPoint);

        if (distance <= radiusInMiles) {
          alert("Inside geofence");
        } else {
          alert("Outside geofence");
        }
      });
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  const addGeofenceCircle = (mapInstance) => {
    // Create a circle using turf.js circle function or custom implementation
    const radiusInKm = radiusInMiles * 1.609344; // Convert miles to km
    const circleData = createCircle(center, radiusInKm);

    mapInstance.addSource("geofence", {
      type: "geojson",
      data: circleData,
    });

    mapInstance.addLayer({
      id: "geofence-fill",
      type: "fill",
      source: "geofence",
      paint: {
        "fill-color": "#FF0000",
        "fill-opacity": 0.35,
      },
    });

    mapInstance.addLayer({
      id: "geofence-border",
      type: "line",
      source: "geofence",
      paint: {
        "line-color": "#FF0000",
        "line-width": 2,
        "line-opacity": 0.8,
      },
    });
  };

  const createCircle = (center, radiusInKm, points = 64) => {
    const coords = [];
    const distanceX =
      radiusInKm / (111.32 * Math.cos((center[1] * Math.PI) / 180));
    const distanceY = radiusInKm / 110.54;

    for (let i = 0; i < points; i++) {
      const theta = (i / points) * (2 * Math.PI);
      const x = distanceX * Math.cos(theta);
      const y = distanceY * Math.sin(theta);
      coords.push([center[0] + x, center[1] + y]);
    }
    coords.push(coords[0]); // Close the circle

    return {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coords],
      },
    };
  };

  const calculateDistance = (point1, point2) => {
    // Calculate distance between two points in miles
    const [lng1, lat1] = point1;
    const [lng2, lat2] = point2;

    const R = 3959; // Earth's radius in miles
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in miles
  };

  const handleAddressSelection = (result, mapInstance) => {
    const coordinates = result.geometry.coordinates;
    const distance = calculateDistance(center, coordinates);

    if (distance <= radiusInMiles) {
      const miles = Math.round(distance);
      const deliveryCharge = chargePerMile.find((m) => m.miles === miles);
      props.updateDeliveryCharge(
        deliveryCharge ? deliveryCharge.charge : chargePerMile[0].charge
      );

      try {
        // Extract address components
        const address = result.place_name;
        let city = "";
        let zipCode = "";

        // Parse context for city and postal code
        if (result.context) {
          result.context.forEach((item) => {
            if (item.id.includes("place")) {
              city = item.text;
            }
            if (item.id.includes("postcode")) {
              zipCode = item.text;
            }
          });
        }

        const updatedAddress = {
          ...selectedAddress,
          address: address,
          city: city,
          zipCode: zipCode,
        };

        dispatch(setSelectedAddress(updatedAddress));
        setDelAddress(address);

        setSwalProps({
          show: true,
          title: "Notification",
          text: "Thank you! Your address is within the delivery 25 miles radius.",
          icon: "success",
        });

        // Add marker to map
        new mapboxgl.Marker().setLngLat(coordinates).addTo(mapInstance);
      } catch (err) {
        console.log(err);
        setSwalProps({
          show: true,
          title: "Notification - Invalid Address",
          text: "Please specify Street Address.",
          icon: "warning",
        });
        props.setServiceOptionToPickup();
        setDelAddress("");
      }
    } else {
      setSwalProps({
        show: true,
        title: "Notification",
        text: "Sorry your location is outside the delivery 25 miles radius!",
        icon: "warning",
      });
    }

    props.setOpen(false);
  };

  return (
    <>
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100vh",
          marginTop: "2vh",
        }}
      >
        <div
          ref={mapContainer}
          style={{
            width: "100%",
            height: "100vh",
          }}
        />
      </div>
      <SweetAlert2 {...swalProps} />
    </>
  );
};
